<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { subscribeUpdateBodySizeCss, unsubscribeUpdateBodySizeCss } from '@@/bits/body_sizing'
import {
  PasteType,
  extractPostAttributesArrayFromClipboardData,
  extractPostAttributesFromClipboardData,
  removeAttachmentFromPostAttributes,
} from '@@/bits/clipboard'
import device from '@@/bits/device'
import { isActiveElementAnInputField, isEventOnAnInputField } from '@@/bits/dom'
import { isAppUsing } from '@@/bits/flip'
import window, { yieldToMain } from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import loadMapApi from '@@/bits/map_api_loader'
import { isPostEmpty } from '@@/bits/post_properties'
import { isNavigatedFromSubmissionRequest } from '@@/bits/surface_share_links_helper'
import { defineAsyncComponent, stableSync } from '@@/bits/vue'
import type { useContentPickerContainerSizeStore } from '@@/pinia/content_picker_container_size'
import type { GlobalSnackbarNotification } from '@@/pinia/global_snackbar'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceMediaPlayerStore } from '@@/pinia/surface_media_player'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { SEARCH_BAR_DEFAULT_TOP, useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceAdditionalSettingsStore } from '@@/pinia/surface_settings_state'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Id } from '@@/types'
import { useSurfaceAddPostButton } from '@@/vuecomposables/surface_add_post_button'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { useSurfaceFetchInitialState } from '@@/vuecomposables/surface_fetch_initial_state'
import {
  focusSurfaceSearchBarInput,
  isFocusedSurfaceSearchBarOffScreen,
} from '@@/vuecomposables/surface_search_bar_focus'
import { useOpenComposerOnArrival } from '@@/vuecomposables/surface_open_composer_on_arrival'
import { useSurfaceUrlShortcuts } from '@@/vuecomposables/surface_url_shortcuts'
import { usePadletPickerCreateState } from '@@/vuecomposables/usePadletPickerLastCreateState'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'
import tinykeys from 'tinykeys'
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'
// import components below statically since it is rendered for most cases
import GlobalAlertDialog from '@@/vuecomponents/GlobalAlertDialog.vue'
import GlobalConfirmationDialog from '@@/vuecomponents/GlobalConfirmationDialog.vue'
import GlobalInputDialog from '@@/vuecomponents/GlobalInputDialog.vue'
import SurfaceActionBar from '@@/vuecomponents/SurfaceActionBar.vue'
import SurfaceAddPost from '@@/vuecomponents/SurfaceAddPost.vue'
import SurfaceContainerHeaderPad from '@@/vuecomponents/SurfaceContainerHeaderPad.vue'
import { useSurfaceNavbarVisibility } from '@@/vuecomposables/surface_navbar_visibility'
import { useAccessibilitySettings } from '@@/vuecomposables/useAccessibilitySettings'
import { useCommandBarEvents } from '@@/vuecomposables/useCommandBarEvents'
import useOnSurfacePostHover from '@@/vuecomposables/useOnSurfacePostHover'
import useOnSurfaceSectionHeaderHover from '@@/vuecomposables/useOnSurfaceSectionHeaderHover'

const SurfacePanels = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePanels.vue'))
const SurfaceActionBarSimplified = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceActionBarSimplified.vue'))
const SurfaceGuestIdLabel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGuestIdLabel.vue'))
const SurfaceContainerGridMasonry = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceContainerGridMasonry.vue'),
)
const SurfaceContainerGrid = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerGrid.vue'))
const SurfaceContainerMap = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerMap.vue'))
const SurfaceContainerTimeline = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerTimeline.vue'))
const SurfaceContainerRows = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerRows.vue'))
const SurfaceContainerMatrix = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerMatrix.vue'))
const SurfaceContainerTable = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerTable.vue'))
const SurfaceContainerStream = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerStream.vue'))
const SurfaceContainerCanvas = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerCanvas.vue'))
const SurfaceContainerBreakout = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerBreakout.vue'))
const SurfaceContainerSubmissionRequest = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceContainerSubmissionRequest.vue'),
)
const SurfaceMagicWallFeedbackPopup = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceMagicWallFeedbackPopup.vue'),
)

// Preloaded for better performance
import '@@/vuecomponents/SurfacePost.vue'

const isMapApiLoaded = ref(false)
const isApp = device.app
const wallContainerMainElementRef = ref<HTMLDivElement | undefined>(undefined)
const reactionIdsByPostId = ref<Record<Id, Id[]>>({})
const headerBackgroundTop = ref<number>(0)
const xSurfacePanels = ref<boolean>(false)

// Scroll smoothness tracking
let scrollTracker: { destroy: () => void } | null = null

const { googleApiKeyForMap, currentCountryCode } = storeToRefs(useSurfaceStartingStateStore())
const { amIOwner, canIPost, amIRegistered, canIComment } = storeToRefs(useSurfacePermissionsStore())
const { xTranslationBanner, xMobileTranslationBanner } = storeToRefs(useSurfaceTranslationsStore())

const surfaceAIChatStore = useSurfaceAIChatStore()
const { magicWallFirstOpen } = storeToRefs(surfaceAIChatStore)

const surfaceStore = useSurfaceStore()
const {
  isEmbedded,
  isOnline,
  format,
  isOverlayVisible,
  isCanvas,
  canUseSections,
  isGrid,
  isMap,
  isScreenshotMode,
  isSimplifiedView,
  isStream,
  isTable,
  isTimelineV1,
  isRows,
  isMatrix,
  isShelf,
  isMobileLayout,
  isDesktopLayout,
  xHeader,
  isSubscriptionPaused,
  headerHeight,
  headerColor,
  isSubmissionRequest,
  isFrozen,
  hasSidePanelOutsideSurface,
  user,
  isSidePanelRounded,
  xNewComposerModalPanel,
} = storeToRefs(surfaceStore)
const { refresh, resizeHeader } = surfaceStore
const surfacePostSearchStore = useSurfacePostSearchStore()
const { isSearchBarInEditMode } = storeToRefs(surfacePostSearchStore)
const { updateSearchBarTop, resetSearchBarTop } = surfacePostSearchStore
const { isContainerSmallerThanTabletPortrait } = storeToRefs(useSurfaceContainerSizeStore())
const { isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())
const surfacePostsStore = useSurfacePostsStore()
const { postBeingEditedCid } = storeToRefs(surfacePostsStore)
const { startNewPost } = surfacePostsStore
const { reactionIdsByPost } = storeToRefs(useReactionsStore())
const { setSnackbar } = useGlobalSnackbarStore()
const { startPickingLocation } = useSurfaceMapStore()
const { startNewDraft, updateActiveDraft, uploadFileActiveDraft } = useSurfaceDraftsStore()
const { isAnyDraftActive, doesActiveDraftHaveAttachment } = storeToRefs(useSurfaceDraftsStore())

const surfaceShareLinksStore = useSurfaceShareLinksStore()
const { showPostPublishedSnackbar } = surfaceShareLinksStore
const { isBreakout, breakoutLinkConfig } = storeToRefs(surfaceShareLinksStore)

const { observeSurfaceContainerSize } = useSurfaceContainerSizeStore()
const { shouldHandlePasteToAddAttachment } = storeToRefs(useSurfacePostPropertiesStore())
const { isGalleryTemplatePanel, xOnboardingPanel } = useSurfaceOnboardingPanelStore()
const { setIsActionBarMounted } = useSurfaceNavbarVisibility()
const { demoPadletPanelVisibility, isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const surfaceGuestStore = useSurfaceGuestStore()

const { setAddPostButtonClickStatus } = useSurfaceAddPostButton()

const { isKeyboardShortcutsEnabled } = useAccessibilitySettings(user)

const { addHoverOnPostShortcuts } = useOnSurfacePostHover()
const { addHoverOnSectionShortcuts } = useOnSurfaceSectionHeaderHover()

// Getters
const isSidePanelDesktop = computed<boolean>(() => {
  return hasSidePanelOutsideSurface.value && !isSmallerThanTabletPortrait.value
})

const isSidePanelDesktopRounded = computed<boolean>(() => {
  return isSidePanelRounded.value && isSidePanelDesktop.value
})

const xMagicWallFeedbackPopup = computed<boolean>(() => {
  return !isMobileLayout.value && magicWallFirstOpen.value
})

function handlePageScroll(e: Event): void {
  updateSearchBarTopOnVerticalScroll(e)
}

// Allow search bar to be scrolled out of the page when scrolling vertically
const pageScrollHandlerTicking = ref<boolean>(false) // throttles handler to run at most once per animation frame.
function updateSearchBarTopOnVerticalScroll(e: Event): void {
  if (
    !pageScrollHandlerTicking.value &&
    !isMap.value && // No scrolling on maps
    !isTimelineV1.value // No vertical scrolling on timeline
  ) {
    window.requestAnimationFrame(() => {
      if (!(e.target instanceof HTMLElement)) return

      pageScrollHandlerTicking.value = false
      // Set header underneath background top position to hide it when the page scrolls the header and wishlist away
      const wallContainerEl = e.target
      headerBackgroundTop.value = -wallContainerEl.scrollTop

      // Search bar is 16px from the top
      // When in edit mode, we keep it fixed so users can scroll to see the posts while searching
      const searchBarTop = SEARCH_BAR_DEFAULT_TOP + (isSearchBarInEditMode.value ? 0 : headerBackgroundTop.value)
      updateSearchBarTop(searchBarTop)
    })

    pageScrollHandlerTicking.value = true
  }
}

async function loadMap(): Promise<void> {
  if (!isMapApiLoaded.value) {
    await loadMapApi(googleApiKeyForMap.value, {
      region: currentCountryCode.value,
    })
    isMapApiLoaded.value = true
  }
}

async function handleFormatChange(): Promise<void> {
  /**
   * Styles for `grid` format (with or without sections) live entirely within Vue components.
   * We don't want to set `data-layout` to `shelf` since it will use old styles and mess up the view.
   */
  useSurfaceMediaPlayerStore().unsetCurrentlyPlayingMedia()

  if (format.value === 'map') {
    await loadMap()
  }

  resetSearchBarTop()

  if (isBreakout.value) {
    document.documentElement.setAttribute('data-layout', format.value === 'map' ? 'map' : 'grid')
    return
  }
  document.documentElement.setAttribute('data-layout', format.value === 'shelf' ? 'grid' : format.value)
}

function enableNewPostHandlers(): void {
  window.addEventListener('paste', handleOnPaste as (e: Event) => void)
}

function disableNewPostHandlers(): void {
  window.removeEventListener('paste', handleOnPaste as (e: Event) => void)
}

function refreshContentIfVisible() {
  if (document.visibilityState === 'visible') {
    refresh({ isAutoRefresh: true })
  }
}

function handleOnPaste(event: ClipboardEvent): void {
  event.stopPropagation()
  if (!canIPost.value) return
  // Check if content picker element is rendered in the DOM. If so, should not handle paste
  const contentPickerEl = document.getElementById('content-picker')
  if (contentPickerEl) return

  // Check if edit thumbnail modal is rendered in the DOM. If so, should not handle paste
  const editThumbnailModalEl = document.getElementById('edit-thumbnail-modal')
  if (editThumbnailModalEl) return

  if (isEventOnAnInputField(event)) {
    return
  }

  let data = extractPostAttributesArrayFromClipboardData(event.clipboardData)

  // When `shouldHandlePasteToAddAttachment` is `false`, we don't handle `PasteType.File`
  // and don't set the `attachment` field in `postAttributes` as it will add the attachment to the draft
  if (!shouldHandlePasteToAddAttachment.value) {
    data = removeAttachmentFromPostAttributes(data)
  }

  if (data.length === 0) return

  // Pasting texts from some applications like Microsoft Word may also paste an image version of the text
  // we want to treat them as text
  const isRichTextPastedAsFile =
    (event.clipboardData?.types?.includes('text/rtf') ?? false) &&
    (event.clipboardData?.types?.includes('Files') ?? false)

  // Create a post with attachment without filename
  // when PasteType is File
  const attachment = extractPostAttributesFromClipboardData(data, PasteType.File)

  if (attachment && !isRichTextPastedAsFile && attachment.postAttributes.file) {
    if (isAnyDraftActive.value && !doesActiveDraftHaveAttachment.value) {
      uploadFileActiveDraft({
        file: attachment.postAttributes.file,
      })
    } else {
      startNewDraft({
        attributes: attachment.postAttributes,
        shouldStartEditing: true,
      })
    }

    trackEvent('Posts', `Added draft by pasting ${attachment.pasteType}`)
    return
  }

  // Create a new post when PasteType is Link or Text or PostContentHash
  const dataWithoutFile = data.filter((datum) => {
    return datum?.pasteType !== PasteType.File
  })

  const isEmptyPost = dataWithoutFile[0] == null || isPostEmpty(dataWithoutFile[0].postAttributes)
  if (isEmptyPost) return

  if (isAnyDraftActive.value && !doesActiveDraftHaveAttachment.value) {
    updateActiveDraft({
      ...dataWithoutFile[0].postAttributes,
    })
  } else {
    startNewDraft({
      attributes: dataWithoutFile[0].postAttributes,
      shouldStartEditing: true,
    })
  }

  trackEvent('Posts', `Added draft by pasting ${dataWithoutFile[0].pasteType}`)
}

function handleOnKeydownC(event: KeyboardEvent): void {
  if (!canIPost.value) return
  if (postBeingEditedCid.value) return
  if (isOverlayVisible.value) return
  if (isActiveElementAnInputField()) return
  event.preventDefault()
  setAddPostButtonClickStatus()

  if (isMap.value) {
    trackEvent('Keyboard Shortcuts', 'Triggered shortcut', null, null, {
      target: 'surface-shortcut-key-c',
      action: 'pick location',
    })
    startPickingLocation()
  } else {
    trackEvent('Keyboard Shortcuts', 'Triggered shortcut', null, null, {
      target: 'surface-shortcut-key-c',
      action: 'start new post',
    })
    startNewPost({})
  }
}

function handleOnKeydownModF(event: KeyboardEvent): void {
  if (postBeingEditedCid.value) return
  if (isOverlayVisible.value) return
  // PRO-6780: defer to native find for non-search inputs and on-screen search inputs; when the
  // focused search bar is scrolled off-screen, fall through and re-focus the visible one (TA-5298).
  if (isActiveElementAnInputField() && !isFocusedSurfaceSearchBarOffScreen()) return
  event.preventDefault()
  focusSurfaceSearchBarInput()
  trackEvent('Surface search', 'Used Ctrl F to search')
}

// Watcher
watch(isOnline, () => {
  refresh({ isAutoRefresh: true })
})

watch(canIPost, (canIPost) => {
  if (canIPost) {
    enableNewPostHandlers()
  } else {
    disableNewPostHandlers()
  }
})

watch(format, () => {
  handleFormatChange()
})

watch(
  reactionIdsByPost,
  (newValue: Record<Id, Id[]>) => {
    stableSync(reactionIdsByPostId.value, newValue)
  },
  { immediate: true },
)

const stopSurfacePanelsWatcher = watch(
  [
    isOverlayVisible,
    xNewComposerModalPanel,
    canIComment,
    () => xTranslationBanner.value,
    () => xMobileTranslationBanner.value,
  ],
  ([
    newIsOverlayVisible,
    newXNewComposerModalPanel,
    newCanIComment,
    newXTranslationBanner,
    newXMobileTranslationBanner,
  ]) => {
    // Set xSurfacePanels as true only if the conditions below are true.
    if (
      newIsOverlayVisible ||
      newXNewComposerModalPanel ||
      newCanIComment ||
      newXTranslationBanner ||
      newXMobileTranslationBanner
    ) {
      xSurfacePanels.value = true
      // We unwatch using a set timeout since we set the watcher to run the callback immediately
      // there could be a race condition where the `surfacePanelWatcher` variable is not yet initialized.
      setTimeout(() => {
        stopSurfacePanelsWatcher()
      }, 0)
    }
  },
  { immediate: true },
)

// #region Keyboard shortcuts
let removeKeyboardShortcutsListener = (): void => {}
function setupKeyboardShortcuts(): void {
  // Remove any existing listeners before setting up new ones
  removeKeyboardShortcutsListener()

  const shortcuts: KeyBindingMap = {
    '$mod+KeyF': handleOnKeydownModF,
  }

  if (!isDemoPadletPanelDesktop.value && isKeyboardShortcutsEnabled.value) {
    shortcuts.KeyC = handleOnKeydownC
    addHoverOnPostShortcuts(shortcuts)
    if (canUseSections.value) {
      addHoverOnSectionShortcuts(shortcuts)
    }
  }

  removeKeyboardShortcutsListener = tinykeys(window, shortcuts)
}

watch([isKeyboardShortcutsEnabled, isDemoPadletPanelDesktop, canUseSections], () => {
  setupKeyboardShortcuts()
})
// #endregion

let contentPickerContainerSizeStore: ReturnType<typeof useContentPickerContainerSizeStore> | null = null
const isReadyToMountSurfaceContainer = ref(false)
const isReadyToMountNotSurfaceContainer = ref(false)

// Lifecycle hooks
onMounted(async () => {
  refresh({ isAutoRefresh: false })

  // Initialize command bar events
  useCommandBarEvents()

  // Watch for size changes in surface container and update surface container size store
  observeSurfaceContainerSize(wallContainerMainElementRef.value as HTMLElement)

  setTimeout(() => {
    import('@@/pinia/content_picker_container_size').then(({ useContentPickerContainerSizeStore }) => {
      contentPickerContainerSizeStore = useContentPickerContainerSizeStore()
      contentPickerContainerSizeStore.observeContainerSize(wallContainerMainElementRef.value as HTMLElement)
    })
  })

  // Ensure new posts are loaded. Realtime might be disconnected when invisible, e.g. on mobile.
  document.addEventListener('visibilitychange', refreshContentIfVisible)

  if (isMap.value) {
    await loadMap()
  }
  nextTick(() => {
    if (canIPost.value) {
      enableNewPostHandlers()
    }
  })
  if (!xHeader.value) {
    // mobile app with native header
    resizeHeader({ height: 0 })
  }

  // Ensure that export as image captures all posts
  if (isScreenshotMode.value && isCanvas.value) {
    const hookToAllStoreChanges = ((store) => store?.subscribe.bind(store))(window.app?.$store)
    const events = ['load', 'beethoven-load', 'error', 'beethoven-error', hookToAllStoreChanges ?? '']
    const extraMarginInPx = 200
    subscribeUpdateBodySizeCss('wish', events, extraMarginInPx)
  }
  if (isSubscriptionPaused.value && amIOwner.value) {
    const snackbarData: GlobalSnackbarNotification = {
      message: __('Your subscription is paused. Your padlets are read-only until it resumes.'),
      persist: true,
    }
    if (!isApp) {
      snackbarData.actionText = __('Go to Settings')
      snackbarData.actionTextActions = [() => navigateTo('/dashboard/settings/billing')]
    }
    setSnackbar(snackbarData)
  }

  setupKeyboardShortcuts()

  if (isNavigatedFromSubmissionRequest() && !isSubmissionRequest.value) {
    showPostPublishedSnackbar()
  }

  usePadletPickerCreateState().resumeIfApplicable()

  await yieldToMain()
  isReadyToMountSurfaceContainer.value = true

  await yieldToMain()
  isReadyToMountNotSurfaceContainer.value = true

  // Initialize scroll smoothness tracking for performance monitoring (dynamically loaded)
  if (wallContainerMainElementRef.value && isAppUsing('scrollSmoothnessTracking')) {
    try {
      import('@@/bits/scroll_smoothness_tracker').then(({ ScrollSmoothnessTracker }) => {
        scrollTracker = new ScrollSmoothnessTracker(wallContainerMainElementRef.value)
      })
    } catch (error) {
      console.warn('Failed to load scroll smoothness tracker:', error)
    }
  }

  // Fetch surface settings after surface loads
  void useSurfaceAdditionalSettingsStore().fetchSurfaceSettings()
})

onBeforeUnmount(() => {
  disableNewPostHandlers()
  unsubscribeUpdateBodySizeCss()
  removeKeyboardShortcutsListener()
  document.removeEventListener('visibilitychange', refreshContentIfVisible)
  if (contentPickerContainerSizeStore != null) {
    contentPickerContainerSizeStore.unobserveContainerSize()
  }
  // Clean up scroll smoothness tracker
  if (scrollTracker) {
    scrollTracker.destroy()
    scrollTracker = null
  }
})
// END LIFECYCLE HOOKS

// On setup
function onSetup(): void {
  if (!device.mobile) {
    useDoubleClickToPost()
  }
}

function hydrateSearchFromQueryParam(): void {
  surfacePostSearchStore.setSearchTermFromQueryParam(new URLSearchParams(window.location.search))
  if (surfacePostSearchStore.searchTerm === '' || isScreenshotMode.value) return
  surfacePostSearchStore.openSearchBarForCurrentLayout()
  surfacePostSearchStore.showPreloadedSearchSnackbar()
}

onSetup()
useSurfaceUrlShortcuts()
useOpenComposerOnArrival()

onBeforeMount(() => {
  hydrateSearchFromQueryParam()
  useSurfaceFetchInitialState().fetchInitialState()
})
</script>

<template>
  <main
    ref="wallContainerMainElementRef"
    :class="[
      'flex',
      isMobileLayout && 'flex-col',
      // In screenshot mode, we want the document to be scrollable so the export services can
      // capture the entire page. Using `100vw` and `100vh` limits this possibility.
      {
        'justify-end': !isScreenshotMode || !isTimelineV1,
        'h-full': isScreenshotMode && !isTimelineV1,
        'w-full overflow-auto': isScreenshotMode,
        'w-screen': !isScreenshotMode,
        'h-vh100': !isScreenshotMode && !(isGalleryTemplatePanel && isSmallerThanTabletPortrait && xOnboardingPanel),
      },
      '@container/surface-container',
    ]"
    :style="{
      // Adding this as a temporary fix to the issue where the surface panels are hidden under the side panels
      // See this issue: https://issues.chromium.org/issues/368873086
      contain: 'layout',
    }"
  >
    <template v-if="isReadyToMountSurfaceContainer">
      <SurfaceContainerHeaderPad
        v-if="isDesktopLayout && !isMap"
        :height="headerHeight"
        :color="headerColor"
        :top="headerBackgroundTop"
      />
      <!-- Submission request with `map` layout will fallback to SurfaceContainerMap below -->
      <SurfaceContainerSubmissionRequest v-if="isSubmissionRequest && !isMap" />
      <!-- Section breakout with `map` layout will fallback to SurfaceContainerMap below -->
      <SurfaceContainerBreakout
        v-else-if="isBreakout && breakoutLinkConfig != null && !isMap"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        :breakout-link-config="breakoutLinkConfig"
        @scroll="handlePageScroll"
      />
      <div v-else-if="isMap" class="w-full">
        <SurfaceContainerMap v-if="isMapApiLoaded" :reaction-ids-by-post-id="reactionIdsByPostId" />
      </div>
      <SurfaceContainerGridMasonry
        v-else-if="isGrid && !isShelf"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        @scroll="handlePageScroll"
      />
      <SurfaceContainerGrid
        v-else-if="isShelf"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        @scroll="handlePageScroll"
      />
      <SurfaceContainerTimeline
        v-else-if="isTimelineV1"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        @scroll="handlePageScroll"
      />
      <SurfaceContainerRows
        v-else-if="isRows"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        @scroll="handlePageScroll"
      />
      <SurfaceContainerMatrix
        v-else-if="isMatrix"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        @scroll="handlePageScroll"
      />
      <SurfaceContainerStream
        v-else-if="isStream"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        @scroll="handlePageScroll"
      />
      <SurfaceContainerTable v-else-if="isTable" @scroll="handlePageScroll" />
      <SurfaceContainerCanvas v-else :reaction-ids-by-post-id="reactionIdsByPostId" @scroll="handlePageScroll" />
    </template>

    <template v-if="isReadyToMountNotSurfaceContainer">
      <template v-if="!isApp && !isEmbedded && !isScreenshotMode">
        <template v-if="!isSubmissionRequest && demoPadletPanelVisibility.showSurfaceActionBar">
          <!-- If the surface topbar is present on desktop layout, we only show the action bar on mobile layout -->
          <!-- Otherwise, we show the action bar on both desktop and mobile layout -->
          <SurfaceActionBar
            v-if="!isSimplifiedView && isMobileLayout"
            :class="['shrink-0', isDesktopLayout && 'w-18 h-full', isMobileLayout && 'w-full']"
            @vue:mounted="setIsActionBarMounted(true)"
          />
          <SurfaceActionBarSimplified
            v-else-if="isSimplifiedView && isMobileLayout"
            :class="['shrink-0', isDesktopLayout && 'w-18 h-full', isMobileLayout && 'w-full']"
            @vue:mounted="setIsActionBarMounted(true)"
          />
        </template>
        <SurfaceGuestIdLabel
          v-else-if="
            surfaceGuestStore.shouldEnableAnonymousAttribution &&
            surfaceGuestStore.isAttributionEnabled &&
            !amIRegistered &&
            !isContainerSmallerThanTabletPortrait
          "
          :class="['fixed top-4 end-4', 'z-modal-dropdown']"
        />
      </template>
      <SurfacePanels v-if="xSurfacePanels" />
      <SurfaceAddPost v-if="!isFrozen && demoPadletPanelVisibility.showSurfaceAddPost" />
      <SurfaceMagicWallFeedbackPopup v-if="xMagicWallFeedbackPopup" />
      <div v-if="isSidePanelDesktopRounded" class="h-4 w-4 end-0 top-0 absolute corner-top pointer-events-none"></div>
      <div
        v-if="isSidePanelDesktopRounded"
        class="h-4 w-4 end-0 bottom-0 absolute corner-bottom pointer-events-none"
      ></div>
      <GlobalAlertDialog />
      <GlobalInputDialog />
      <GlobalConfirmationDialog />
    </template>
  </main>
</template>

<style lang="scss" scoped>
@import '@@/styles/3/modules/all';
.corner-top {
  background: radial-gradient(circle at bottom left, transparent 16px, black 16px);
  z-index: $sidepanel-zindex;

  [dir='rtl'] & {
    background: radial-gradient(circle at bottom right, transparent 16px, black 16px);
  }
}

.corner-bottom {
  background: radial-gradient(circle at top left, transparent 16px, black 16px);
  z-index: $sidepanel-zindex;

  [dir='rtl'] & {
    background: radial-gradient(circle at top right, transparent 16px, black 16px);
  }
}
</style>
